/**
 * Pure banner-state logic for the customer-friendly subscription UX (no DB / no React).
 *
 * Access is governed by the banked balance (continuous burn), so banners INFORM — they never
 * gate. Priority: a payment failure outranks a low/empty balance (fixing the card is the action
 * that matters most). The threshold/strings live here so UI + any future email share one source.
 */
// Trading days at/under which we surface the "keep your access" nudge. Set to 5 (2026-07-14): the
// live base clusters at a median of 5 granted days, so a 3-day threshold showed the nudge to almost
// no one before their access lapsed. 5 catches them while they still have days to convert on.
export const LOW_BALANCE_THRESHOLD = 5; // trading days

/**
 * How long after signup we say NOTHING about money. Owner, 2026-08-10, on the new-user flow:
 * "premium access ends immediately we should give them 1 day automatically so they are not getting
 * asked for money immediately after subscribing". A conversion nudge in the first two days is not
 * a nudge, it is a bill arriving before the product has been seen. The nudge still fires with 3+
 * days of balance left, which is the window it was tuned for.
 *
 * 🚨 SUPERSEDED FOR low_balance BY THE OWNER, 2026-10-22. This window never actually applied in production:
 * the route fed accountAgeHours from a createdAt that parsed to NaN, so 253 of 291 low_balance asks since
 * 09-04 reached accounts under 48h old, and 5 of the 55 who clicked became Stripe customers. Shown that, the
 * owner chose to KEEP the day-0 ask (register: quiet-window-never-enforced-createdat-is-a-date-1022).
 * low_balance no longer checks this window ON PURPOSE. It still bounds: the first-session offer (it IS the
 * first-48h card), win_recap, and app/api/user/peak-desire-trial.
 */
export const NEW_ACCOUNT_QUIET_HOURS = 48;

/**
 * Hours since users.created_at, or null when it cannot be read. Takes `unknown` ON PURPOSE: the column is declared
 * mode:'string' but the driver returns a Date (probed on prod 2026-10-22), and the old inline
 * `Date.parse(String(v).replace(' ', 'T') + 'Z')` turned every Date into NaN. Accepts a Date, a naive-UTC string
 * ("2026-10-22 21:19:52.68"), or an explicit-offset string.
 */
export function accountAgeHoursOf(createdAt: unknown, nowMs: number = Date.now()): number | null {
  let ms = NaN;
  if (createdAt instanceof Date) ms = createdAt.getTime();
  else if (typeof createdAt === 'string' && createdAt.trim()) {
    const s = createdAt.trim();
    ms = Date.parse(/[zZ]$|[+-]\d\d:?\d\d$/.test(s) ? s : s.replace(' ', 'T') + 'Z');
  }
  return Number.isFinite(ms) ? (nowMs - ms) / 3_600_000 : null;
}

/**
 * ⭐ THE SUBSCRIBER QUIET RULE (owner, 2026-08-15): "not on already subscribed, need to make sure we
 * do not bother them, part of the incentive is being free of nudges unless they are a couple of days
 * from expiring and not auto renewing, then we ask, but that would be it."
 *
 * So a subscriber sees NOTHING from this banner except two things:
 *   1. `payment_failed` — kept deliberately. That is not a nudge, it is a service alert: their card
 *      broke and access lapses if nobody fixes it. Suppressing it would mean the first they hear of
 *      it is losing access they paid for.
 *   2. `expiring_no_renew` — the one sanctioned ask, below.
 *
 * Everything else (`low_balance`, `out_of_days`) is already non-subscriber-only and stays that way.
 * ⚠ Being nudge-free is now a STATED BENEFIT of subscribing. Any future banner must check
 * isSubscriber before it renders, or it silently spends that promise.
 */
export const EXPIRING_NO_RENEW_DAYS = 2; // "a couple of days from expiring"

export type PremiumBannerKind = 'none' | 'payment_failed' | 'low_balance' | 'out_of_days' | 'expiring_no_renew' | 'trial_ending' | 'activate_days' | 'win_recap' | 'first_session_trial';

export interface PremiumBannerInput {
  gatingEnabled: boolean;     // banners only matter once the paid model is live
  isSubscriber: boolean;
  daysRemaining: number;
  paymentFailed: boolean;     // a renewal invoice failed and hasn't recovered
  /** hasGrantedAccess(email) — unlimited comp access (owner/support grants). NEVER nag these
   *  accounts about balance (2026-07-08: the owner, whose badge says "∞ Unlimited", got a
   *  false "3 premium days left — top up" the moment the banner became visible). */
  granted?: boolean;
  /** True when the user still has to verify their email. The <VerifyEmailBanner/> is already
   *  saying the one thing that matters to them ("verify to unlock 5 more days"), so this banner
   *  stays silent — otherwise a brand-new account gets TWO stacked bars, the second of which asks
   *  for a credit card. One bar, one action. */
  pendingEmailVerification?: boolean;
  /** Hours since users.created_at. Under NEW_ACCOUNT_QUIET_HOURS it enables the first-session offer and
   *  holds back win_recap. It does NOT suppress low_balance (owner, 2026-10-22: keep the day-0 ask). */
  accountAgeHours?: number | null;
  /** users.subscription_cancel_at_period_end — Stripe says this sub will NOT auto-renew. */
  cancelAtPeriodEnd?: boolean | null;
  /** Whole days until the subscription lapses, from users.subscription_current_period_end. Negative
   *  or zero means the period has already run out. Null when we do not know, which SUPPRESSES the
   *  expiry ask rather than guessing — a wrong "you are about to lose access" to a paying customer is
   *  worse than saying nothing. */
  daysUntilPeriodEnd?: number | null;
  /** Card-on-file free-trial length IF this user is eligible for one, else 0/undefined. Computed by
   *  the caller from the SAME isTrialEligible() the checkout uses, so the ask promises a trial only
   *  when the checkout will actually grant it (no false promise to a churned ex-subscriber). When
   *  > 0, the two non-subscriber asks lead with "start a free trial, $0 today" instead of a cold
   *  "subscribe $/mo" — the same proven framing the /subscribe page carries, brought to where
   *  non-subscribers actually are (/alerts, /chart). Register: `card-on-file-trial-0822`. */
  trialEligibleDays?: number;
  /** Whole days until a card-on-file free trial ends, from users.subscription_trial_ends_at. A NUMBER
   *  means "this subscriber is on a trial" and drives the trial_ending reminder; null/undefined means
   *  not on a trial (a converted, paying subscriber carries null and never sees the reminder). The
   *  caller computes it so this stays pure. */
  trialDaysRemaining?: number | null;
  /** Pre-formatted trial-end date for the copy, e.g. "August 29". Computed by the caller so the pure
   *  function never has to touch a timezone or the clock. */
  trialEndsAtLabel?: string;
  /** users.premium_activated_at IS NOT NULL — the user has accessed premium at least once, so their
   *  days are burning. FALSE/undefined with a positive balance means "days granted but never used"
   *  (dormant), which is the reactivation nudge's whole audience. Register:
   *  `activate-dormant-days-0822`. */
  everActivated?: boolean;
  /** "Ask at the win" (register: ask-at-the-win-scope-0905). Present ONLY when the caller has decided
   *  this user should see the personalized win-ask: flag on, non-subscriber, tasted, A/B TREATMENT arm,
   *  and a real win row in win_recaps. The caller does all that gating (and the control arm gets null,
   *  so it keeps the generic ask and the click-rate stays comparable). When present it replaces
   *  low_balance/out_of_days at the same moment. `pct` is the run since the alert; `symbol`/`alertDateLabel`
   *  are the user's OWN alerted stock and the alert date. */
  winRecap?: { symbol: string; pct: number; alertDateLabel: string } | null;
  /** FIRST-SESSION TRIAL OFFER (stripe-starts-happen-day0-1021). TRUE only when the caller has decided this user
   *  is in the A/B's OFFER arm AND the owner switch is on. It is the one sanctioned exception to the 48h quiet
   *  rule; the control arm passes false and keeps the quiet rule exactly as before. */
  firstSessionOffer?: boolean;
  /** TASTE-SLICE TREATMENT account inside its no-burn window that HAS received live premium data (caller checks
   *  realtime_access_log). Register tasting-premium-is-the-real-lever-0819, owner 2026-11-04: these accounts never
   *  burn, so they never reach LOW_BALANCE_THRESHOLD and got NO day-0 ask. TRUE lets low_balance fire at their real
   *  (unburned) balance, the same moment and copy the control arm gets. The caller also passes everActivated true. */
  tasteWindow?: boolean;
}

export interface PremiumBannerState {
  kind: PremiumBannerKind;
  daysRemaining: number;
  title: string;
  message: string;
  cta?: { label: string; href: string };
}

const EMPTY: PremiumBannerState = { kind: 'none', daysRemaining: 0, title: '', message: '' };

/**
 * The CTA link the inline card renders. A state whose href already carries its own query (e.g. the first-session
 * offer's ?from=first-session-offer) keeps it; a bare href gets the surface tag. Before this the card appended
 * "?from=moment:<surface>" blindly and produced "...?from=first-session-offer?from=moment:alerts" (review 2026-10-21).
 */
export function inlineCtaHref(href: string, surface: string): string {
  return href.includes('?') ? `${href}&surface=${encodeURIComponent(surface)}` : `${href}?from=moment:${surface}`;
}

export function premiumBannerState(input: PremiumBannerInput): PremiumBannerState {
  const days = Math.max(0, Math.floor(input.daysRemaining || 0));
  if (!input.gatingEnabled) return { ...EMPTY, daysRemaining: days }; // pre-launch: never show
  // Unlimited (granted) accounts have nothing to top up — day balance is irrelevant to them.
  if (input.granted) return { ...EMPTY, daysRemaining: days };
  // Unverified: the verify banner owns the screen. Never stack a paywall bar under it.
  if (input.pendingEmailVerification) return { ...EMPTY, daysRemaining: days };

  // 1.5) TRIAL ENDING — a card-on-file free trial with a known end date. This is a SERVICE FACT about
  // an upcoming charge (same category as payment_failed and expiring_no_renew), NOT a sales nudge, so
  // it is one of the few states a subscriber is permitted to see. It fires ONLY when trialDaysRemaining
  // is a number, which the caller sets only from subscription_trial_ends_at while status='trialing';
  // a converted paying subscriber carries null there and never sees this. Placed just below
  // payment_failed (a broken card outranks a scheduled charge) and above everything else.
  if (typeof input.trialDaysRemaining === 'number' && !input.paymentFailed) {
    const d = Math.max(0, Math.floor(input.trialDaysRemaining));
    const on = input.trialEndsAtLabel ? ` on ${input.trialEndsAtLabel}` : '';
    const cancelling = input.cancelAtPeriodEnd === true;
    return {
      kind: 'trial_ending',
      daysRemaining: days,
      title: d <= 0 ? 'Your free trial ends today' : `Your free trial ends in ${d} day${d === 1 ? '' : 's'}`,
      message: cancelling
        ? `Auto-renew is off, so your access ends when the trial does${on}. Turn renewal back on before then to keep your real-time alerts, the full scanner and live charts.`
        : `Your card will be charged${on} to keep real-time alerts, the full scanner and live charts. Cancel anytime before then and you will not be charged.`,
      cta: { label: cancelling ? 'Keep my subscription' : 'Manage subscription', href: '/subscribe' },
    };
  }

  // 1) Payment failure takes precedence — they keep access, but should fix the card.
  if (input.paymentFailed) {
    return {
      kind: 'payment_failed',
      daysRemaining: days,
      title: 'Payment failed',
      message: "We couldn't process your latest payment. Update your card to keep your subscription active. Your access continues in the meantime.",
      cta: { label: 'Update your card', href: '/subscribe' },
    };
  }

  // 2) THE ONE ASK A SUBSCRIBER IS ALLOWED TO SEE. They cancelled, so auto-renew is off and access
  // genuinely lapses on a known date. Within EXPIRING_NO_RENEW_DAYS of it we say so once. This is a
  // service fact before it is a sales ask, which is exactly why it is the only one permitted: they
  // chose to end it, and the alternative is access disappearing with no warning.
  // Requires BOTH signals. Unknown period end → say nothing (see daysUntilPeriodEnd).
  if (
    input.isSubscriber &&
    input.cancelAtPeriodEnd === true &&
    typeof input.daysUntilPeriodEnd === 'number' &&
    input.daysUntilPeriodEnd <= EXPIRING_NO_RENEW_DAYS
  ) {
    const d = Math.max(0, Math.floor(input.daysUntilPeriodEnd));
    return {
      kind: 'expiring_no_renew',
      daysRemaining: days,
      title: d <= 0 ? 'Your subscription ends today' : `Your subscription ends in ${d} day${d === 1 ? '' : 's'}`,
      message:
        'Auto-renew is off, so your premium access stops when the current period ends. Turn renewal back on any time before then and nothing changes.',
      cta: { label: 'Keep my subscription', href: '/subscribe' },
    };
  }

  // ⭐ PAST THIS POINT NOTHING MAY FIRE FOR A SUBSCRIBER. Both remaining states are explicitly
  // non-subscriber. See EXPIRING_NO_RENEW_DAYS for why that is a promise and not just a default.

  // 2.5) ACTIVATE DORMANT DAYS — the reactivation nudge. A verified non-subscriber who has premium
  // days but has NEVER used a premium feature (everActivated false) is sitting on free value they
  // have not felt. Measured 2026-08-22: 759 such users. This is NOT a sales ask (no price, CTA opens
  // the product, not /subscribe), so it is exempt from the 48h new-account quiet window and it
  // OUTRANKS low_balance/out_of_days: to a never-taster, "5 days left, subscribe" is the wrong
  // message — they have not started, so the message is "your days are waiting, go spend one". Once
  // they taste (everActivated true) this stops and the normal balance states resume. Register:
  // activate-dormant-days-0822.
  // ⚠ `=== false`, NOT `!== true`: it fires only when the caller EXPLICITLY determined the user has
  // never activated. Undefined (a caller that did not compute it) must fall through to the normal
  // balance states, or every existing low_balance/none case flips to this nudge.
  if (!input.isSubscriber && days > 0 && input.everActivated === false) {
    return {
      kind: 'activate_days',
      daysRemaining: days,
      title: `You have ${days} premium day${days === 1 ? '' : 's'} waiting`,
      message: 'Your free premium days are ready and have not started counting yet. Open the live '
        + 'scanner and real-time alerts to see what is moving right now. Days only burn on days you '
        + 'actually use the site, so looking now costs you nothing.',
      cta: { label: "See what's live", href: '/alerts' },
    };
  }

  // 2.6) FIRST-SESSION TRIAL OFFER (stripe-starts-happen-day0-1021). 69% of Stripe trial starts happen in the first
  // 24h, mostly from /alerts, and the 48h quiet rule below silences every ask then. For the A/B OFFER arm only
  // (caller sets firstSessionOffer), a brand-new, verified (pendingEmailVerification returned above), trial-eligible
  // non-subscriber who has ALREADY TASTED (seen live alerts; never-tasters got activate_days above) is offered the
  // card-on-file trial, once, calmly. Only inside the quiet window: after 48h the normal states take over unchanged.
  // ⚠ No "keep your free days" claim: grant days keep burning during a trial (burnOnAccess has no subscriber check).
  {
    const trialF = Math.max(0, Math.floor(input.trialEligibleDays || 0));
    const brandNewF = typeof input.accountAgeHours === 'number' && input.accountAgeHours < NEW_ACCOUNT_QUIET_HOURS;
    if (!input.isSubscriber && input.firstSessionOffer === true && brandNewF && trialF > 0 && days > 0 && input.everActivated === true) {
      return {
        kind: 'first_session_trial',
        daysRemaining: days,
        title: `Start your ${trialF}-day free trial`,
        message: `You are seeing alerts as they fire. Start a ${trialF}-day free trial, $0 today, to keep real-time alerts, the full scanner and live charts. Cancel anytime before it ends and you pay nothing.`,
        cta: { label: 'Start free trial', href: '/subscribe?from=first-session-offer' },
      };
    }
  }

  // 2.7) WIN-RECAP - "ask at the win" (register: ask-at-the-win-scope-0905). A non-subscriber IN THE ASK
  // WINDOW whose OWN alerted symbol ran up. Present only for the A/B TREATMENT arm (the caller gates flag
  // + tasted + arm + a real win in win_recaps); the control arm carries null and keeps the generic
  // low_balance/out_of_days ask, so the CLICK RATE of the two is directly comparable - the metric this is
  // measured on (conversion is unreadable at ~10/cohort, mid-taste-moment-design-0904). Fires at the SAME
  // moment (days <= threshold) it replaces, respects the 48h new-account quiet window, non-subscriber only.
  const brandNewAcct = typeof input.accountAgeHours === 'number' && input.accountAgeHours < NEW_ACCOUNT_QUIET_HOURS;
  if (!input.isSubscriber && input.winRecap && days <= LOW_BALANCE_THRESHOLD && !brandNewAcct) {
    const w = input.winRecap;
    const trialM = Math.max(0, Math.floor(input.trialEligibleDays || 0));
    return {
      kind: 'win_recap',
      daysRemaining: days,
      title: `$${w.symbol} ran +${Math.round(w.pct)}% since your alert`,
      message: days <= 0
        ? `You flagged it on ${w.alertDateLabel}. That is exactly what the scanner catches in real time, but your premium days ran out, so the next one goes by without you.`
        : `You flagged it on ${w.alertDateLabel}. That is exactly what the scanner catches in real time. Keep premium and catch the next one the moment it moves.`,
      cta: { label: trialM > 0 ? 'Start free trial' : 'Keep full access', href: '/subscribe' },
    };
  }

  // A trial-eligible non-subscriber is offered the card-on-file trial instead of a cold paid ask.
  // trialN > 0 ONLY when the caller confirmed eligibility (isTrialEligible), so this never promises
  // a trial a churned ex-subscriber will not get. Same $0-today framing as the /subscribe page.
  const trialN = Math.max(0, Math.floor(input.trialEligibleDays || 0));

  // 3) Out of days — dropped to the delayed free tier until they subscribe. Lead with what they
  // lost (the real-time edge), not a neutral "renew".
  if (days <= 0 && !input.isSubscriber) {
    return {
      kind: 'out_of_days',
      daysRemaining: 0,
      title: 'Your premium access has ended',
      message: trialN > 0
        ? `You're on the delayed free tier. Start a ${trialN}-day free trial, $0 today, and restore real-time unusual-volume alerts, the full 11,000-symbol scanner and live charts. Cancel anytime.`
        : "You're on the delayed free tier. Subscribe to restore real-time unusual-volume alerts, the full 11,000-symbol scanner and live charts.",
      cta: { label: trialN > 0 ? 'Start free trial' : 'Restore full access', href: '/subscribe' },
    };
  }

  // 4) Low balance — surface at/under the threshold, escalating on the last day. NOT for
  // auto-renewing subscribers (their renewal tops the balance up). Copy leads with the VALUE at
  // risk (the scanner edge) + urgency — this is the highest-intent conversion moment.
  // ⭐ NO 48h quiet check here, deliberately (owner, 2026-10-22, see NEW_ACCOUNT_QUIET_HOURS). A new user reaches
  // 5 days on their first burn, so this is the day-0 ask, and it is the CONTROL arm of the first-session A/B:
  // the OFFER arm sees first_session_trial above instead. Re-adding the check silences the control arm.
  // A taste-slice treatment account in its window sits ABOVE the threshold only because it does not burn
  // (tasteWindow above), so it gets this ask at its real balance too.
  if (days > 0 && (days <= LOW_BALANCE_THRESHOLD || input.tasteWindow === true) && !input.isSubscriber) {
    const lastDay = days <= 1;
    return {
      kind: 'low_balance',
      daysRemaining: days,
      title: `${days} premium day${days === 1 ? '' : 's'} left`,
      message: trialN > 0
        ? (lastDay
            ? `Last day of full access. Start a ${trialN}-day free trial, $0 today, to keep real-time alerts, the full scanner and live charts. Cancel anytime.`
            : `Keep the real-time scanner and unusual-volume alerts that flag movers early. Start a ${trialN}-day free trial, $0 today, cancel anytime.`)
        : (lastDay
            ? 'Last day of full access. Subscribe now to keep real-time alerts, the full scanner and live charts before they switch off.'
            : 'Keep the real-time scanner and unusual-volume alerts that flag movers early. Subscribe before your access lapses.'),
      cta: { label: trialN > 0 ? 'Start free trial' : (lastDay ? 'Keep my access' : 'Keep full access'), href: '/subscribe' },
    };
  }

  return { ...EMPTY, daysRemaining: days };
}
